import { Check, KeyRound, LifeBuoy, ListTodo, RefreshCcw, Wrench } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { RowMenu } from '@/components/common/RowMenu'
import { EmptyState } from '@/components/common/States'
import { Avatar } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import { fmtDate, fmtMoney } from '../../shared/format'
import { useToggleFollowup } from '../api'
import type { Followup, Renewal, RenewalKind } from '../types'
import { toastError, useCrm } from '../ui'
import { ChannelLabel, RenewalStatusBadge } from './CrmBits'
import { CustomerLink } from './RecordLists'

const KIND: Record<RenewalKind, { icon: typeof KeyRound; tone: string }> = {
  license: { icon: KeyRound, tone: 'bg-primary/10 text-primary' },
  maintenance: { icon: Wrench, tone: 'bg-violet/12 text-violet' },
  support: { icon: LifeBuoy, tone: 'bg-info/12 text-info' },
}

/** Kalan gün rozeti: geçmiş kırmızı, 30 gün içinde sarı. */
function DaysLeft({ r }: { r: Renewal }) {
  const { t } = useTranslation()
  if (r.status === 'renewed' || r.status === 'cancelled') return null
  const tone = r.daysLeft < 0 ? 'bg-destructive/10 text-destructive' : r.daysLeft <= 30 ? 'bg-warning/15 text-warning-strong' : 'bg-muted text-muted-foreground'
  const label = r.daysLeft < 0 ? t('crm.renewal.overdueDays', { count: -r.daysLeft }) : r.daysLeft === 0 ? t('crm.renewal.today') : t('crm.renewal.daysLeft', { count: r.daysLeft })
  return <span className={cn('rounded-md px-2 py-0.5 text-xs font-medium tabular-nums', tone)}>{label}</span>
}

type RenewalProps = { rows: Renewal[]; showCustomer?: boolean; canWrite: boolean; onEdit: (r: Renewal) => void; onDelete: (r: Renewal) => void }

export function RenewalList({ rows, showCustomer, canWrite, onEdit, onDelete }: RenewalProps) {
  const { t } = useTranslation()
  if (!rows.length) return <EmptyState icon={RefreshCcw} title={t('crm.renewal.empty')} />
  return (
    <ul className="divide-y">
      {rows.map((r) => {
        const { icon: Icon, tone } = KIND[r.kind]
        return (
          <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
            <div className="flex min-w-0 flex-1 basis-60 items-center gap-3">
              <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg', tone)}>
                <Icon className="size-4" aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold">{t(`crm.renewalKind.${r.kind}`)}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {showCustomer ? (
                    <>
                      <CustomerLink id={r.customerId} tab="orders" /> ·{' '}
                    </>
                  ) : null}
                  {t('crm.renewal.dueShort', { date: fmtDate(r.dueOn) })}
                  {r.notes ? ` · ${r.notes}` : ''}
                </p>
              </div>
            </div>
            <DaysLeft r={r} />
            <span className="w-28 text-right text-sm font-semibold tabular-nums">{r.amount === '0.00' ? <span className="text-xs font-normal text-muted-foreground">{t('crm.renewal.noAmount')}</span> : fmtMoney(r.amount)}</span>
            <RenewalStatusBadge status={r.status} />
            {canWrite ? <RowMenu label={t(`crm.renewalKind.${r.kind}`)} onEdit={() => onEdit(r)} onDelete={() => onDelete(r)} /> : null}
          </li>
        )
      })}
    </ul>
  )
}

type FollowupProps = { rows: Followup[]; showCustomer?: boolean; canWrite: boolean; onEdit: (f: Followup) => void; onDelete: (f: Followup) => void }

export function FollowupList({ rows, showCustomer, canWrite, onEdit, onDelete }: FollowupProps) {
  const { t } = useTranslation()
  const { staffName } = useCrm()
  const toggle = useToggleFollowup()
  if (!rows.length) return <EmptyState icon={ListTodo} title={t('crm.followup.empty')} />
  return (
    <ul className="divide-y">
      {rows.map((f) => (
        <li key={f.id} className={cn('flex items-start gap-3 px-4 py-3', f.done && 'bg-muted/30')}>
          <button
            type="button"
            role="checkbox"
            aria-checked={f.done}
            aria-label={f.note}
            disabled={!canWrite || toggle.isPending}
            onClick={() => toggle.mutate(f.id, { onError: toastError })}
            className={cn(
              'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default',
              f.done ? 'border-success bg-success text-white' : 'border-muted-foreground/40 hover:border-primary',
            )}
          >
            {f.done ? <Check className="size-3" strokeWidth={3} aria-hidden /> : null}
          </button>
          <div className="min-w-0 flex-1">
            <p className={cn('text-sm font-medium', f.done && 'text-muted-foreground line-through')}>{f.note}</p>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {showCustomer ? <CustomerLink id={f.customerId} tab="overview" /> : null}
              <span className={cn('tabular-nums', f.overdue && 'font-medium text-destructive')}>
                {fmtDate(f.dueOn)}
                {f.overdue ? ` · ${t('crm.followup.overdue')}` : ''}
              </span>
              <ChannelLabel channel={f.channel} small />
              <span className="inline-flex items-center gap-1">
                <Avatar name={staffName(f.ownerId)} className="size-4 text-[8px]" />
                {staffName(f.ownerId)}
              </span>
            </p>
          </div>
          {canWrite ? <RowMenu label={f.note} onEdit={() => onEdit(f)} onDelete={() => onDelete(f)} /> : null}
        </li>
      ))}
    </ul>
  )
}
